/**
 * Public unsubscribe page — CAN-SPAM / GDPR compliance (v2.16.40 BUG-051).
 *
 * Unauthenticated. Reached via /unsubscribe?t=<HMAC token> from the
 * footer link in every marketing email we send. Shows the recipient
 * which email is being unsubscribed, a one-click confirm button, and
 * a "you're all set" confirmation. If the token is tampered or
 * expired we surface a clear error without leaking which emails
 * exist in our system.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, MailX, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function PublicUnsubscribe() {
    const [params] = useSearchParams();
    const token = params.get('t') || '';
    const [state, setState] = useState('loading'); // loading | ready | already | done | error
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (!token) {
            setState('error');
            setErrorMsg('Missing unsubscribe token. Please use the link from your email.');
            return;
        }
        axios.get(`${API}/public/unsubscribe/status`, { params: { t: token } })
            .then(res => {
                setEmail(res.data?.email || '');
                setState(res.data?.unsubscribed ? 'already' : 'ready');
            })
            .catch(err => {
                setState('error');
                setErrorMsg(err.response?.data?.detail || 'This unsubscribe link is invalid or has been tampered with.');
            });
    }, [token]);

    const confirm = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/public/unsubscribe?t=${encodeURIComponent(token)}`);
            setState('done');
        } catch (err) {
            setErrorMsg(err.response?.data?.detail || 'Something went wrong. Please try again.');
            setState('error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
                {state === 'loading' && (
                    <div className="flex flex-col items-center gap-3 py-8">
                        <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
                        <p className="text-sm text-stone-500">Checking your subscription…</p>
                    </div>
                )}

                {state === 'ready' && (
                    <>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 mb-4 mx-auto">
                            <MailX className="w-6 h-6 text-stone-700" />
                        </div>
                        <h1 className="text-xl font-bold text-stone-900 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Unsubscribe
                        </h1>
                        <p className="text-sm text-stone-600 text-center mt-2">
                            Unsubscribe <strong>{email}</strong> from our marketing emails?
                        </p>
                        <p className="text-xs text-stone-500 text-center mt-2">
                            You won't receive future campaign emails or sequences. Transactional messages (like password resets) will still come through.
                        </p>
                        <button
                            onClick={confirm}
                            disabled={submitting}
                            className="w-full mt-6 px-4 py-2.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium transition-colors disabled:opacity-60"
                        >
                            {submitting ? 'Unsubscribing…' : 'Confirm unsubscribe'}
                        </button>
                        <p className="text-[11px] text-stone-400 text-center mt-3">
                            Changed your mind? Just close this page.
                        </p>
                    </>
                )}

                {state === 'already' && (
                    <>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4 mx-auto">
                            <CheckCircle2 className="w-6 h-6 text-green-700" />
                        </div>
                        <h1 className="text-xl font-bold text-stone-900 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Already unsubscribed
                        </h1>
                        <p className="text-sm text-stone-600 text-center mt-2">
                            <strong>{email}</strong> is already opted out. You won't receive any more marketing emails from us.
                        </p>
                    </>
                )}

                {state === 'done' && (
                    <>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4 mx-auto">
                            <CheckCircle2 className="w-6 h-6 text-green-700" />
                        </div>
                        <h1 className="text-xl font-bold text-stone-900 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            You're unsubscribed
                        </h1>
                        <p className="text-sm text-stone-600 text-center mt-2">
                            <strong>{email}</strong> will no longer receive marketing emails from us. It may take up to a few minutes for in-flight messages to stop.
                        </p>
                    </>
                )}

                {state === 'error' && (
                    <>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-4 mx-auto">
                            <XCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <h1 className="text-xl font-bold text-stone-900 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Invalid link
                        </h1>
                        <p className="text-sm text-stone-600 text-center mt-2">{errorMsg}</p>
                    </>
                )}
            </div>
        </div>
    );
}
